/**
 * Shared header component with four layout variants:
 *   simple   — logo + panel button left, nav centered, auth right
 *   centered — logo centered, nav below, auth right
 *   wide     — full-width, logo + panel button left, nav left, auth right
 *   minimal  — logo + panel button left, auth right, no nav
 *
 * All variants share identical functionality:
 *   - Side panel button anchored left next to logo (signed-in only, when sidePanel provided)
 *   - Theme toggle (always visible, right side)
 *   - Auth slot: account menu (signed-in) or sign in/up buttons (signed-out)
 *   - Optional inline nav links between left and right zones
 *   - Mobile dropdown menu
 *   - Scroll behaviour hook
 *
 * Side panel rendered as a sibling outside the header element to avoid
 * z-index / overflow clipping.
 */

'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { LogOut, LogIn, Star, User, Menu, ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme/theme-toggle'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

// ─── Types ────────────────────────────────────────────────────────────────────

export type HeaderLayout = 'simple' | 'centered' | 'wide' | 'minimal'

export interface HeaderLink {
  label: string
  href?: string
  icon?: React.ReactNode
  external?: boolean
  showWhen?: 'signed-in' | 'signed-out'
  children?: HeaderLink[]
}

function filterLinks(
  links: HeaderLink[],
  isSignedIn: boolean | undefined,
): HeaderLink[] {
  return links.filter((link) => {
    switch (link.showWhen) {
      case 'signed-in': return isSignedIn === true
      case 'signed-out': return isSignedIn === false
      default: return true
    }
  })
}

export interface HeaderUIProps {
  layout?: HeaderLayout
  logo: React.ReactNode
  logoHref?: string
  links?: HeaderLink[]
  // Auth — omit isSignedIn entirely to hide the auth slot
  isSignedIn?: boolean
  email?: string | null
  avatarUrl?: string | null
  showPricingLink?: boolean
  onSignOut?: () => void
  accountMenuItems?: { label: string; href: string; icon?: React.ReactNode }[]
  scrollBehavior?: 'static' | 'sticky' | 'elevated' | 'hide-on-scroll'
  // Side panel — rendered outside the header, receives open state from header
  sidePanel?: React.ReactNode | ((isOpen: boolean, onClose: () => void) => React.ReactNode)
  sidePanelIcon?: React.ReactNode
}

// ─── Scroll behaviour ─────────────────────────────────────────────────────────

interface ScrollState {
  headerClassName: string
  headerStyle: React.CSSProperties
}

function useScrollBehavior(
  behavior: HeaderUIProps['scrollBehavior'] = 'hide-on-scroll',
): ScrollState {
  const [scrolled, setScrolled] = useState(false)
  const [visible, setVisible] = useState(true)
  const lastScrollY = useRef(0)

  useEffect(() => {
    if (behavior === 'static') return

    const onScroll = () => {
      const y = window.scrollY

      if (behavior === 'elevated') {
        setScrolled(y > 0)
      }

      if (behavior === 'hide-on-scroll') {
        setScrolled(y > 0)
        if (y > lastScrollY.current && y > 80) {
          setVisible(false)
        } else if (y < lastScrollY.current) {
          setVisible(true)
        }
        lastScrollY.current = y
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [behavior])

  if (behavior === 'static') {
    return { headerClassName: 'relative', headerStyle: {} }
  }
  if (behavior === 'sticky') {
    return { headerClassName: 'sticky top-0 z-50 border-b', headerStyle: {} }
  }
  if (behavior === 'elevated') {
    return {
      headerClassName: cn(
        'sticky top-0 z-50 transition-shadow duration-300',
        scrolled && 'shadow-[0_0_20px_0_rgba(0,0,0,0.1)] [&]:border-b-transparent',
      ),
      headerStyle: {},
    }
  }
  // hide-on-scroll
  return {
    headerClassName: cn(
      'sticky top-0 z-50 transition-transform duration-300',
      scrolled && 'border-b border-border',
    ),
    headerStyle: {
      transform: visible ? 'translateY(0)' : 'translateY(-110%)',
    },
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LogoLink({ logo, href = '/' }: { logo: React.ReactNode; href?: string }) {
  return (
    <Link href={href} className="flex items-center shrink-0">
      {logo}
    </Link>
  )
}

/**
 * Left zone — logo + optional side panel button.
 * Side panel button only renders when signed in and a panel is provided.
 */
function LeftZone({
  logo,
  logoHref,
  hasSidePanel,
  onOpenSidePanel,
  sidePanelIcon,
}: {
  logo: React.ReactNode
  logoHref?: string
  hasSidePanel: boolean
  onOpenSidePanel: () => void
  sidePanelIcon?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-4 shrink-0">
      {hasSidePanel && (
        <Button
          variant="default"
          size="icon-sm"
          onClick={onOpenSidePanel}
          aria-label="Open navigation"
          className="hidden lg:inline-flex rounded-full"
        >
          {sidePanelIcon ?? <Menu className="h-4 w-4" />}
        </Button>
      )}
      <LogoLink logo={logo} href={logoHref} />
    </div>
  )
}
function NavItem({ link, onClick }: { link: HeaderLink; onClick?: () => void }) {
  const [open, setOpen] = useState(false)

  // Dropdown variant
  if (link.children?.length) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              open && "bg-accent text-accent-foreground"  // same styles as hover
            )}
          >
            {link.icon && <span className="mr-1.5 inline-flex items-center">{link.icon}</span>}
            {link.label}
            {/* <ChevronDown className={cn("ml-1 h-3.5 w-3.5 opacity-60 transition-transform duration-200", open && "rotate-180")} /> */}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={8}>
          {link.children.map((child, i) => (
            <DropdownMenuItem key={i} asChild>
              <Link href={child.href ?? '#'}>
                {child.icon && <span className="mr-2">{child.icon}</span>}
                {child.label}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  // Plain link variant (unchanged)
  return (
    <Button variant="ghost" size="sm" asChild>
      <Link
        href={link.href ?? '#'}
        target={link.external ? '_blank' : undefined}
        rel={link.external ? 'noopener noreferrer' : undefined}
        onClick={onClick}
      >
        {link.icon && <span className="mr-1.5 inline-flex items-center">{link.icon}</span>}
        {link.label}
      </Link>
    </Button>
  )
}

// function NavItem({ link, onClick }: { link: HeaderLink; onClick?: () => void }) {
//   return (
//     <Button variant="ghost" size="default" asChild>
//       <Link
//         href={link.href}
//         target={link.external ? '_blank' : undefined}
//         rel={link.external ? 'noopener noreferrer' : undefined}
//         onClick={onClick}
//         className="text-sm font-medium transition-colors duration-150"
//       >
//         {link.icon && (
//           <span className="mr-1.5 inline-flex items-center">{link.icon}</span>
//         )}
//         {link.label}
//       </Link>
//     </Button>
//   )
// }

function AccountMenu({
  email,
  onSignOut,
  extraItems,
}: {
  email?: string | null
  avatarUrl?: string | null
  onSignOut?: () => void
  extraItems?: HeaderUIProps['accountMenuItems']
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="icon-sm"
          className="rounded-full"
          aria-label="User menu"
        >
          <User className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-72">
        <DropdownMenuLabel>
         User
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/account"><User />Account</Link>
        </DropdownMenuItem>
        {extraItems?.map((item, i) => (
          <DropdownMenuItem key={i} asChild>
            <Link href={item.href}>
              {item.icon && <span className="mr-2">{item.icon}</span>}
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={onSignOut}>
          <LogOut />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Right zone — theme toggle + account menu or sign in/up buttons.
 * No side panel button here — it lives in LeftZone next to the logo.
 * isSignedIn === undefined hides the auth slot entirely (theme toggle still shows).
 */
function RightZone({
  isSignedIn,
  email,
  avatarUrl,
  showPricingLink,
  onSignOut,
  accountMenuItems,
}: {
  isSignedIn?: boolean
  email?: string | null
  avatarUrl?: string | null
  showPricingLink?: boolean
  onSignOut?: () => void
  accountMenuItems?: HeaderUIProps['accountMenuItems']
}) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <ThemeToggle />
      {isSignedIn === true && (
        <AccountMenu
          email={email}
          avatarUrl={avatarUrl}
          onSignOut={onSignOut}
          extraItems={accountMenuItems}
        />
      )}
      {isSignedIn === false && (
        <>
          <Button variant="secondary" size="sm" asChild>
            <Link href="/sign-in">Sign in</Link>
          </Button>
          <Button variant="default" size="sm" asChild>
            <Link href={showPricingLink ? '/pricing' : '/sign-up'}>
              {showPricingLink ? 'Get started' : 'Sign up'}
            </Link>
          </Button>
        </>
      )}
    </div>
  )
}

/**
 * Mobile menu — shown on small screens.
 * Theme toggle sits outside the dropdown alongside it.
 * Side panel button sits between theme toggle and dropdown when signed in.
 */
function MobileMenu({
  links = [],
  isSignedIn,
  showPricingLink,
  onSignOut,
  accountMenuItems,
  email,
  hasSidePanel,
  onOpenSidePanel,
  sidePanelIcon,
}: {
  links?: HeaderLink[]
  isSignedIn?: boolean
  email?: string | null
  showPricingLink?: boolean
  onSignOut?: () => void
  accountMenuItems?: HeaderUIProps['accountMenuItems']
  hasSidePanel: boolean
  onOpenSidePanel: () => void
  sidePanelIcon?: React.ReactNode
}) {
  return (
    <div className="lg:hidden flex items-center gap-2">

      <ThemeToggle />

      {/* Signed-in: account menu with User icon */}
      {isSignedIn === true && (
        <AccountMenu
          email={email}
          onSignOut={onSignOut}
          extraItems={accountMenuItems}
        />
      )}

      {/* Signed-out: sign in / get started buttons or dropdown */}
      {links.length > 0 && (
        <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="default" size="icon-sm" aria-label="Open menu" className="rounded-full">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M2 5h14M2 9h14M2 13h14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="w-52">
          {links.map((link, i) => (
            link.children?.length ? (
              <div key={i}>
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2">
                  {link.label}
                </DropdownMenuLabel>
                {link.children.map((child, j) => (
                  <DropdownMenuItem key={j} asChild>
                    <Link href={child.href ?? '#'}>{child.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </div>
            ) : (
              <DropdownMenuItem key={i} asChild>
                <Link href={link.href ?? '#'}>{link.label}</Link>
              </DropdownMenuItem>
            )
          ))}
          {!isSignedIn && (
            <>
              {links.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem asChild>
                <Link href="/sign-in">Sign in</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href={showPricingLink ? '/pricing' : '/sign-up'}>
                  {showPricingLink ? 'Get started' : 'Sign up'}
                </Link>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      )}
      
      {/* Sidebar nav button — rightmost, signed-in only */}
      {isSignedIn === true && hasSidePanel && (
        <Button
          variant="default"
          size="icon-sm"
          onClick={onOpenSidePanel}
          aria-label="Open navigation"
          className="rounded-full"
        >
          {sidePanelIcon ?? <Menu className="h-4 w-4" />}
        </Button>
      )}

    </div>
  )
}

// ─── Shared inner props ───────────────────────────────────────────────────────

interface InnerProps {
  p: HeaderUIProps
  scroll: ScrollState
  hasSidePanel: boolean
  onOpenSidePanel: () => void
}

function leftZoneProps(p: HeaderUIProps, hasSidePanel: boolean, onOpenSidePanel: () => void) {
  return {
    logo: p.logo,
    logoHref: p.logoHref,
    hasSidePanel,
    onOpenSidePanel,
    sidePanelIcon: p.sidePanelIcon,
  }
}

function rightZoneProps(p: HeaderUIProps) {
  return {
    isSignedIn: p.isSignedIn,
    email: p.email,
    avatarUrl: p.avatarUrl,
    showPricingLink: p.showPricingLink,
    onSignOut: p.onSignOut,
    accountMenuItems: p.accountMenuItems,
  }
}

function mobileMenuProps(p: HeaderUIProps, hasSidePanel: boolean, onOpenSidePanel: () => void) {
  return {
    links: p.links ?? [],
    isSignedIn: p.isSignedIn,
    email: p.email,
    showPricingLink: p.showPricingLink,
    onSignOut: p.onSignOut,
    accountMenuItems: p.accountMenuItems,
    hasSidePanel,
    onOpenSidePanel,
    sidePanelIcon: p.sidePanelIcon,
  }
}

const baseHeader = 'w-full bg-background text-foreground'

// ─── Layout variants ──────────────────────────────────────────────────────────

/**
 * simple — logo + panel button left, nav centered, auth right
 */
function HeaderSimple({ p, scroll, hasSidePanel, onOpenSidePanel }: InnerProps) {
  const links = p.links ?? []

  return (
    <header className={cn(baseHeader, scroll.headerClassName)} style={scroll.headerStyle}>
      <div className="mx-auto max-w-6xl px-6 h-16 flex items-center gap-6">

        <LeftZone {...leftZoneProps(p, hasSidePanel, onOpenSidePanel)} />

        {/* Inline nav — centered between left and right zones */}
        {links.length > 0 && (
          <nav className="hidden lg:flex items-center gap-2 flex-1 justify-center">
            {links.map((link, i) => <NavItem key={i} link={link} />)}
          </nav>
        )}
        {links.length === 0 && <div className="flex-1" />}

        <div className="hidden lg:flex">
          <RightZone {...rightZoneProps(p)} />
        </div>

        <div className="ml-auto lg:hidden">
          <MobileMenu {...mobileMenuProps(p, hasSidePanel, onOpenSidePanel)} />
        </div>

      </div>
    </header>
  )
}

/**
 * centered — logo centered, nav below, auth right
 */
function HeaderCentered({ p, scroll, hasSidePanel, onOpenSidePanel }: InnerProps) {
  const links = p.links ?? []

  return (
    <header className={cn(baseHeader, scroll.headerClassName)} style={scroll.headerStyle}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center">

        <LeftZone {...leftZoneProps(p, hasSidePanel, onOpenSidePanel)} />

        <div className="flex-1 flex justify-center">
          <LogoLink logo={p.logo} href={p.logoHref} />
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex">
            <RightZone {...rightZoneProps(p)} />
          </div>
          <div className="lg:hidden">
            <MobileMenu {...mobileMenuProps(p, hasSidePanel, onOpenSidePanel)} />
          </div>
        </div>

      </div>

      {/* Inline nav sits below the main row for centered layout */}
      {links.length > 0 && (
        <div className="hidden lg:flex justify-center border-t border-border/50">
          <nav className="flex items-center gap-8 h-11">
            {links.map((link, i) => <NavItem key={i} link={link} />)}
          </nav>
        </div>
      )}
    </header>
  )
}

/**
 * wide — full-width, logo + panel button left, nav left after logo, auth right
 */
function HeaderWide({ p, scroll, hasSidePanel, onOpenSidePanel }: InnerProps) {
  const links = p.links ?? []

  return (
    <header className={cn(baseHeader, scroll.headerClassName)} style={scroll.headerStyle}>
      <div className="w-full px-8 h-16 flex items-center gap-8">

        <LeftZone {...leftZoneProps(p, hasSidePanel, onOpenSidePanel)} />

        {links.length > 0 && (
          <nav className="hidden lg:flex items-center gap-2">
            {links.map((link, i) => <NavItem key={i} link={link} />)}
          </nav>
        )}

        <div className="ml-auto">
          <div className="hidden lg:flex">
            <RightZone {...rightZoneProps(p)} />
          </div>
          <div className="lg:hidden">
            <MobileMenu {...mobileMenuProps(p, hasSidePanel, onOpenSidePanel)} />
          </div>
        </div>

      </div>
    </header>
  )
}

/**
 * minimal — logo + panel button left, auth right, no inline nav
 */
function HeaderMinimal({ p, scroll, hasSidePanel, onOpenSidePanel }: InnerProps) {
  return (
    <header className={cn(baseHeader, scroll.headerClassName)} style={scroll.headerStyle}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-14 flex items-center">

        <LeftZone {...leftZoneProps(p, hasSidePanel, onOpenSidePanel)} />

        <div className="ml-auto">
          <div className="hidden lg:flex">
            <RightZone {...rightZoneProps(p)} />
          </div>
          <div className="lg:hidden">
            <MobileMenu {...mobileMenuProps(p, hasSidePanel, onOpenSidePanel)} />
          </div>
        </div>

      </div>
    </header>
  )
}

// ─── Root export ──────────────────────────────────────────────────────────────

export function HeaderUI(props: HeaderUIProps) {
  const scroll = useScrollBehavior(props.scrollBehavior ?? 'hide-on-scroll')
  const [sidePanelOpen, setSidePanelOpen] = useState(false)

  // Side panel only active when signed in and a panel is provided
  const hasSidePanel = !!props.sidePanel && props.isSignedIn === true
  const onOpenSidePanel = () => setSidePanelOpen(true)
  // const onCloseSidePanel = () => setSidePanelOpen(false)
  const onCloseSidePanel = useCallback(() => setSidePanelOpen(false), [])

  const visibleLinks = filterLinks(props.links ?? [], props.isSignedIn)

  const resolvedProps = { ...props, links: visibleLinks }

  const renderedPanel = props.sidePanel
    ? typeof props.sidePanel === 'function'
      ? props.sidePanel(sidePanelOpen, onCloseSidePanel)
      : props.sidePanel
    : null

  const innerProps: InnerProps = {
    p: resolvedProps,
    scroll,
    hasSidePanel,
    onOpenSidePanel,
  }

  return (
    <>
      {(() => {
        switch (props.layout ?? 'simple') {
          case 'simple': return <HeaderSimple   {...innerProps} />
          case 'centered': return <HeaderCentered {...innerProps} />
          case 'wide': return <HeaderWide     {...innerProps} />
          case 'minimal': return <HeaderMinimal  {...innerProps} />
          default: return <HeaderSimple   {...innerProps} />
        }
      })()}

      {/* Rendered outside the header to avoid z-index / overflow clipping */}
      {renderedPanel}
    </>
  )
}
